/**
 * Entity cloning utilities.
 * Provides deep clone operations for ODA entities.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";

/**
 * Result of a deep clone operation.
 */
export interface ClonedEntityInfo {
  entityId: DrawingModule.OdDbObjectId;
  layerName: string;
}

/**
 * Deep clone entities using ODA's deepCloneObjects.
 *
 * IMPORTANT: Do NOT use entity.clone() + appendOdDbEntity() - it doesn't work
 * correctly in DrawingJS. Use this function instead.
 *
 * @param module The drawing module
 * @param db The database
 * @param entityIds Array of entity IDs to clone
 * @param transform Optional transformation matrix to apply to cloned entities
 * @returns Array of cloned entity info (ID + layer name)
 */
export function deepCloneEntities(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  entityIds: DrawingModule.OdDbObjectId[],
  transform?: DrawingModule.OdGeMatrix3d,
): ClonedEntityInfo[] {
  const result: ClonedEntityInfo[] = [];

  if (entityIds.length === 0) return result;

  // 1. Create array of source entity IDs
  const sourceIds = new module.OdDbObjectIdArray();
  for (const entityId of entityIds) {
    if (entityId && !entityId.isNull()) {
      sourceIds.push_back(entityId);
    }
  }

  if (sourceIds.size() === 0) {
    deleteObj(sourceIds);
    return result;
  }

  try {
    // 2. Create ID mapping for deep clone (kDcCopy = 0)
    let idMap: DrawingModule.OdDbIdMapping | null = null;
    try {
      idMap = module.OdDbIdMapping.createObject1(0 as any);
      idMap.setDestDb(db);

      // 3. Deep clone entities to current space
      const currentSpaceId = getCurrentSpaceId(db);
      db.deepCloneObjects(sourceIds, currentSpaceId, idMap, false);

      // 4. Resolve cloned IDs by looking up each source ID in the mapping.
      //
      // ODA pattern from ExTransform.cpp (_Mirror_func, lines 379-390):
      //   for each source ID, create an OdDbIdPair(sourceId) and call idMap.compute()
      //   to retrieve the cloned ID. Do NOT use idMap.newIterator() — it returns ALL
      //   objects cloned by deepCloneObjects (reactors, dictionaries…), not just
      //   entities, and OdDbEntity.cast() returns null for non-entity objects.
      for (const sourceId of entityIds) {
        if (!sourceId || sourceId.isNull()) continue;

        let pair: DrawingModule.OdDbIdPair | null = null;
        let entity: DrawingModule.OdDbEntity | null = null;
        try {
          pair = module.OdDbIdPair.ctr1(sourceId);
          const found = idMap.compute(pair);

          if (found && pair.isCloned()) {
            const newId = pair.value();

            // Open entity for write to apply transformations
            entity = openAs(newId, module.OdDbEntity, OpenMode.kForWrite);

            if (entity) {
              // Apply transformation if provided
              if (transform && entity.transformBy) {
                entity.transformBy(transform);
              }

              entity.recordGraphicsModified(true);

              // Get layer name
              const layerOdStr = entity.layer();
              const layerName = layerOdStr.toString();
              deleteObj(layerOdStr);

              result.push({
                entityId: newId,
                layerName,
              });
            }
          }
        } finally {
          if (entity) deleteObj(entity);
          if (pair) deleteObj(pair);
        }
      }
    } finally {
      if (idMap) deleteObj(idMap);
    }
  } finally {
    deleteObj(sourceIds);
  }

  return result;
}

/**
 * Create a translation matrix.
 * Helper for common clone-and-offset operations.
 */
export function createTranslationMatrix(
  module: DrawingModuleType,
  offsetX: number,
  offsetY: number,
  offsetZ: number = 0,
): DrawingModule.OdGeMatrix3d {
  const vec = new module.OdGeVector3d(offsetX, offsetY, offsetZ);
  const matrix = module.OdGeMatrix3d.translation(vec);
  deleteObj(vec);
  return matrix;
}

/**
 * Calculate the combined extents of multiple entities.
 * Useful for determining offset when duplicating.
 */
export function getCombinedExtents(
  module: DrawingModuleType,
  entityIds: DrawingModule.OdDbObjectId[],
): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} | null {
  if (entityIds.length === 0) return null;

  const combinedExtent = new module.OdGeExtents3d();

  for (const entityId of entityIds) {
    if (entityId && !entityId.isNull()) {
      const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForRead);
      const entityExtent = new module.OdGeExtents3d();
      entity.getGeomExtents(entityExtent);
      combinedExtent.addExt(entityExtent);
      deleteObj(entityExtent);
      deleteObj(entity);
    }
  }

  const min = combinedExtent.minPoint();
  const max = combinedExtent.maxPoint();

  const result = {
    minX: min.x,
    minY: min.y,
    maxX: max.x,
    maxY: max.y,
    width: max.x - min.x,
    height: max.y - min.y,
  };

  deleteObj(min);
  deleteObj(max);
  deleteObj(combinedExtent);

  return result;
}
